import React, { useRef, useState } from 'react';
import { Download, Table, Share } from 'lucide-react';
import type { CategoryScore } from '../../types/survey-student';

interface BackgroundResponse {
  name?: string;
  email?: string;
  classLevel?: string;
  hometown?: string;
  firstLanguage?: string;
  englishProficiency?: number;
}

interface SurveySummaryProps {
  scores: CategoryScore[];
  backgroundData: BackgroundResponse;
  onRestart: () => void;
}

// Ring Chart Component
interface RingChartProps {
  score: number;
  maxScore: number;
  color: string;
  size?: number;
}

const RingChart: React.FC<RingChartProps> = ({ score, maxScore, color, size = 120 }) => {
  const percentage = (score / maxScore) * 100;
  const radius = (size - 20) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="10"
          fill="transparent"
          strokeLinecap="round"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="10"
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      {/* Score text in center */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-gray-900">{score}</span>
        <span className="text-xs text-gray-500">/ {maxScore}</span>
      </div>
    </div>
  );
};

// Combined Ring Chart Component
interface CombinedRingChartProps {
  scores: CategoryScore[];
  size?: number;
}

const CombinedRingChart: React.FC<CombinedRingChartProps> = ({ scores, size = 400 }) => {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  // Sort scores by value in ascending order (lowest to highest)
  const sortedScores = [...scores].sort((a, b) => a.score - b.score);
  
  const centerX = size / 2;
  const centerY = size / 2;
  const maxRadius = (size - 60) / 2;
  
  // Reverse the array so highest score is at the end (will be center)
  const reversedScores = [...sortedScores].reverse();
  
  const displayedCategory = selectedCategory || hoveredCategory;
  
  return (
    <div className="relative flex items-center justify-center group" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {reversedScores.map((category, index) => {
          // Calculate radius for this ring
          const ringWidth = Math.max(6, (category.score / 7) * 20); // Min width 6px, max based on score
          const radius = maxRadius - (index * 15); // Space rings 15px apart to fit all 15
          
          if (radius <= 0) return null;
          
          const color = categoryColors[category.name] || '#6b7280';
          const isHighlighted = displayedCategory === category.name;
          const opacity = displayedCategory && !isHighlighted ? 0.3 : 1;
          
          if (index === reversedScores.length - 1) {
            // Center circle (highest score) - filled circle
            return (
              <circle
                key={category.name}
                cx={centerX}
                cy={centerY}
                r={Math.max(15, radius / 4)} // Smaller center circle to fit more rings
                fill={color}
                fillOpacity={opacity * 0.9}
                className="transition-all duration-1000 ease-out"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredCategory(category.name)}
                onMouseLeave={() => setHoveredCategory(null)}
                onClick={() => setSelectedCategory(selectedCategory === category.name ? null : category.name)}
              />
            );
          } else {
            // Rings - strokes with width based on score, no outline
            return (
              <circle
                key={category.name}
                cx={centerX}
                cy={centerY}
                r={radius}
                stroke={color}
                strokeWidth={ringWidth}
                strokeOpacity={opacity}
                fill="transparent"
                className="transition-all duration-1000 ease-out"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredCategory(category.name)}
                onMouseLeave={() => setHoveredCategory(null)}
                onClick={() => setSelectedCategory(selectedCategory === category.name ? null : category.name)}
              />
            );
          }
        })}
      </svg>
      
      {/* Tooltip for hovered/selected category */}
      {displayedCategory && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-xs text-center z-10">
          <div className="text-sm font-semibold text-gray-900 mb-1">
            {displayedCategory}
          </div>
          <div className="text-xs text-gray-600">
            Score: {scores.find((s: CategoryScore) => s.name === displayedCategory)?.score}/7
          </div>
        </div>
      )}
      
      {/* Legend */}
      <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
        <div className="text-xs font-medium text-gray-700 text-center">
          Hover or click rings to explore • Top 5 shown →
        </div>
      </div>
    </div>
  );
};

interface CategoryGroup {
  name: string;
  description: string;
  categories: string[];
}

const categoryGroups: CategoryGroup[] = [
  {
    name: 'Value Component',
    description: 'How you value and engage with course material',
    categories: ['Intrinsic Goal Orientation', 'Extrinsic Goal Motivation', 'Task Value']
  },
  {
    name: 'Expectancy Component',
    description: 'Your beliefs about learning ability and success',
    categories: ['Control of Learning Beliefs', 'Self-Efficacy for Learning and Performance']
  },
  {
    name: 'Affective Component',
    description: 'Emotional aspects of learning',
    categories: ['Test Anxiety']
  },
  {
    name: 'Cognitive and Metacognitive Strategies',
    description: 'How you process and regulate learning',
    categories: ['Rehearsal', 'Organization', 'Elaboration', 'Critical Thinking', 'Metacognitive Self-Regulation']
  },
  {
    name: 'Resource Management Strategies',
    description: 'How you manage learning resources and environment',
    categories: ['Time and Study Environment', 'Effort Regulation', 'Peer Learning', 'Help Seeking']
  }
];

// Color palette for different categories
const categoryColors: Record<string, string> = {
  'Intrinsic Goal Orientation': '#3b82f6', // Blue
  'Extrinsic Goal Motivation': '#8b5cf6', // Purple
  'Task Value': '#06b6d4', // Cyan
  'Control of Learning Beliefs': '#10b981', // Emerald
  'Self-Efficacy for Learning and Performance': '#f59e0b', // Amber
  'Test Anxiety': '#ef4444', // Red
  'Rehearsal': '#84cc16', // Lime
  'Elaboration': '#22c55e', // Green
  'Organization': '#14b8a6', // Teal
  'Critical Thinking': '#6366f1', // Indigo
  'Metacognitive Self-Regulation': '#a855f7', // Violet
  'Time and Study Environment': '#ec4899', // Pink
  'Effort Regulation': '#f97316', // Orange
  'Peer Learning': '#06b6d4', // Cyan
  'Help Seeking': '#8b5cf5' // Purple
};

// const getScoreInterpretation = (score: number) => {
//   if (score <= 3) return { level: 'Low', color: 'text-red-600', bgColor: 'bg-red-50' };
//   if (score <= 5) return { level: 'Medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
//   return { level: 'High', color: 'text-green-600', bgColor: 'bg-green-50' };
// };

export const SurveySummary: React.FC<SurveySummaryProps> = ({ scores, backgroundData, onRestart }) => {
  const summaryRef = useRef<HTMLDivElement>(null);
  const [showTable, setShowTable] = useState(false);

  const handleSaveImage = async () => {
    if (summaryRef.current) {
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(summaryRef.current, {
          backgroundColor: '#f9fafb',
          scale: 2,
        });
        
        const link = document.createElement('a');
        link.download = 'survey-summary.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (error) {
        console.error('Error saving image:', error);
        alert('Error saving image. Please try again.');
      }
    }
  };

  const handleCopyTable = () => {
    const tableData = scores.map(score => 
      `${score.name}\t${score.score}\t${score.maxPossible}\t${(score as any).items?.join(', ') || 'N/A'}`
    ).join('\n');
    
    const headers = 'Category\tScore\tMax Score\tQuestions\n';
    navigator.clipboard.writeText(headers + tableData);
  };

  const handleShareWithInstructor = () => {
    // Create a summary text for sharing
    const summaryText = `Learning Profile Summary for ${backgroundData.name || 'Student'}

Top 5 Characteristics:
${[...scores]
  .sort((a, b) => b.score - a.score)
  .slice(0, 5)
  .map((category, index) => `${index + 1}. ${category.name}: ${category.score}/7`)
  .join('\n')}

All Scores:
${scores.map(score => `${score.name}: ${score.score}/7`).join('\n')}

Contact: ${backgroundData.email || 'Not provided'}
Class Level: ${backgroundData.classLevel || 'Not provided'}`;

    navigator.clipboard.writeText(summaryText);
    alert('Summary copied to clipboard! You can now paste it in an email to your instructor.');
  };

  return (
    <div className="space-y-8">
      <div ref={summaryRef} className="space-y-8 bg-gray-50 p-8 rounded-lg">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Learning Color Aura</h2>
          <p className="text-gray-600">
            Below are your mean scores for each category (scale: 1-7). 
            Higher scores indicate stronger alignment with each characteristic.
          </p>
          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>Low (1-3)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <span>Medium (3-5)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>High (5-7)</span>
            </div>
          </div>
        </div>

        {/* Combined Ring Chart */}
        <div className="flex flex-col items-center space-y-6 mb-12">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Overall Profile</h3>
            <p className="text-sm text-gray-600">
              Combined view of all categories ranked by score. Ring width represents relative strength.
            </p>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <CombinedRingChart scores={scores} size={400} />
            
            {/* Legend - Top 5 only */}
            <div className="grid grid-cols-1 gap-3 max-w-sm">
              <div className="text-sm font-semibold text-gray-900 mb-2 text-center">
                Top 5 Characteristics
              </div>
              {[...scores]
                .sort((a, b) => b.score - a.score)
                .slice(0, 5)
                .map((category, index) => {
                  const color = categoryColors[category.name] || '#6b7280';
                  return (
                    <div key={category.name} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate leading-tight">
                          {category.name}
                        </div>
                        <div className="text-sm text-gray-500 leading-tight">
                          {category.score} / 7
                        </div>
                      </div>
                      <div className="text-sm font-bold text-gray-600 ml-1">
                        #{index + 1}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {showTable ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Questions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scores.map((score, index) => (
                  <tr key={score.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{score.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{score.score}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{score.maxPossible}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(score as any).items?.join(', ') || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-12">
            {categoryGroups.map((group) => (
              <div key={group.name} className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{group.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                </div>
                
                <div className="grid gap-6">
                  {scores
                    .filter(score => group.categories.includes(score.name))
                    .map((category) => {
                      const color = categoryColors[category.name] || '#6b7280';
                      
                      return (
                        <div 
                          key={category.name} 
                          className="bg-white rounded-lg shadow-md border border-gray-100 p-6"
                        >
                          <div className="flex items-start gap-6">
                            {/* Ring Chart */}
                            <div className="flex-shrink-0">
                              <RingChart
                                score={category.score}
                                maxScore={category.maxPossible}
                                color={color}
                                size={100}
                              />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 space-y-3">
                              <div className="flex justify-between items-start">
                                <h4 className="text-lg font-medium text-gray-900">
                                  {category.name}
                                </h4>
                              </div>
                              
                              <p className="text-sm text-gray-600">{category.description}</p>
                            
                              <div className="text-xs text-gray-500">
                                Questions: {(category as any).items?.join(', ') || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-center gap-4">
        <button
          type="button"
          onClick={onRestart}
          className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Start New Survey
        </button>
        <button
          type="button"
          onClick={handleSaveImage}
          className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <Download className="w-5 h-5" />
          Save as Image
        </button>
        <button
          type="button"
          onClick={() => {
            setShowTable(!showTable);
            if (showTable) {
              handleCopyTable();
            }
          }}
          className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2"
        >
          <Table className="w-5 h-5" />
          {showTable ? 'Copy Table Data' : 'Show as Table'}
        </button>
        <button
          type="button"
          onClick={handleShareWithInstructor}
          className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <Share className="w-5 h-5" />
          Share with Instructor
        </button>
      </div>
    </div>
  );
};